import { convertAverage } from '@/app/features/reports/ratingHelpers';
import { useScrollContext } from '@/app/features/reports/contexts/scroll-context';
import { TestsIcon } from '@/components/raytd/icons';
import { Pill } from '@/components/raytd/pill';
import { RatingMixedScore } from '@/components/raytd/rating-tool';
import { ValuePill } from '@/components/raytd/value-pill';
import { cn } from '@/lib/utils';
import clsx from 'clsx';
import { ArrowDown, ArrowRight, ChevronRight } from 'lucide-react';
import React, { memo, useCallback, useMemo } from 'react';
import { FlattenedItem } from '../utils/entities';
import useReportViewerContext, { useReportViewContext } from './report-viewer-context';

interface RowHeaderProps {
    item: FlattenedItem;
    toggleExpand: (id: string) => void;
    active: boolean;
    parents?: string[];
    className?: string;
}

const ScoreDisplay = memo(({ score, count }: { score: string, count: number }) => (
    <div className="min-w-12 md:w-24 flex flex-row justify-center items-center bg-zinc-50">
        <Pill
            variant="enabled"
            size="md"
            className="text-xs font-normal bg-transparent"
        >
            {score === undefined ? '' : (
                <>
                    {`${score}`}
                    <span className="md:inline hidden">&nbsp;/&nbsp;5.00</span>
                    <span className="md:inline hidden">&nbsp;({count})</span>
                </>
            )}
        </Pill>
    </div>
));

const styles = {
    md: { // Medium (default) size
        item: "text-[14px] text-zinc-950",
        lastItem: "text-[14px] tracking-normal",
        lastItemMuted: "text-[14px] text-zinc-600",
    },
    sm: { // Small size
        item: "text-xs text-zinc-700",
        lastItem: "text-xs tracking-wide",
        lastItemMuted: "text-xs text-zinc-500",
    },
};

function PathMenuLink({ id, title, level }: { id: string, title: string, level: number }) {

    const { setMenu } = useScrollContext();

    const handleClick = useCallback((e: React.MouseEvent<HTMLSpanElement>) => {
        e.stopPropagation();
        const rect = (e.target as HTMLSpanElement).getBoundingClientRect();
        setMenu({
            open: true,
            x: rect.right, // position menu next to button
            y: rect.bottom,
            header: { id, title, level }
        });
    }, [id, level]);

    return <span 
        onClick={handleClick} 
        className="hover:underline cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(e as any);
            }
        }}
    >
        <ChevronRight className="text-zinc-950 mt-0.5 size-4 mx-1 flex-shrink-0" />
    </span>;
}

function PathBreadcrumbLink({ id, title, level }: { id: string, title: string, level: number }) {
    const { scrollToHeader } = useScrollContext();

    const handleClick = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        scrollToHeader(id, level);
    }, [id, scrollToHeader, level]);

    return <span 
        onClick={handleClick} 
        className="hover:underline cursor-pointer"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleClick(e as any);
            }
        }}
    >
        {title}
    </span>;
}

type PathBreadcrumbProps = {
    parents: string[];
    hasContents?: boolean;
    /** The size variant to use for styling the text. Defaults to 'md'. */
    size?: keyof typeof styles;
    groupHeaderId?: string;
    /**
     * SDA reports drop the "Category >" prefix on their sticky sub-category header — only the
     * sub-category name renders, with no breadcrumb chain to the parent Category (raytd/web QA
     * 108.03, sub-category header). The underlying two-level Category -> Sub-category grouping
     * (and `parents`) stays intact for section boundaries/ordering; only the rendered label
     * changes. A single-segment `parents` (the top-level Category header itself) already renders
     * as just that one segment, so this only visibly changes deeper headers.
     */
    isSdaFormat?: boolean;
};

const PathBreadcrumb = memo(({ parents, hasContents = false, size = 'md', groupHeaderId, isSdaFormat = false }: PathBreadcrumbProps) => {
    const styleSet = styles[size];
    const { showMissingData } = useReportViewerContext();

    const isMissingText = useCallback((text: string) => {
        return (text === '(Missing Location)' || text === '(Missing Element)' || text === '(Missing Rating)') && showMissingData;
    }, [showMissingData]);

    const visibleParents = isSdaFormat ? parents.slice(-1) : parents;

    return (
        <div className="flex items-center w-full min-w-0">
            {visibleParents.map((parent, index) => {
                const isLastItem = index === visibleParents.length - 1;
                const isMissing = isMissingText(parent);

                if (isLastItem) {
                    return (
                        <span
                            key={index}
                            title={parent}
                            className={clsx(
                                "min-w-0 shrink-[0.2] truncate font-medium text-zinc-950",
                                styleSet.lastItem,
                                {
                                    "bg-red-400 text-white font-semibold px-2 py-1 rounded-md": isMissing,
                                }
                            )}
                        >
                            {parent}
                        </span>
                    );
                }

                return (
                    <React.Fragment key={index}>
                        {/* Ancestors shrink with the room available (all segments in proportion to their
                            length) instead of each being cut at a fixed 96px, so headers of the same test
                            truncate consistently, and give way before the last segment (the one that names
                            the group); the full text is on hover (raytd/web#232). */}
                        <div title={parent} className="min-w-8 shrink flex items-center">
                            <span
                                className={clsx(
                                    "truncate inline-block text-zinc-500",
                                    styleSet.item
                                )}
                            >
                                <PathBreadcrumbLink id={groupHeaderId} title={parent} level={index} />
                            </span>
                        </div>
                        <PathMenuLink id={groupHeaderId} title={parent} level={index} />
                    </React.Fragment>
                );
            })}
        </div>
    );
});


interface ExpandableItem {
    id: string;
    parents: string[];
    hasContent: boolean;
    hasChildren: boolean;
    isExpanded: boolean;
    // An optional ID for connecting ARIA attributes
    contentId?: string;
}

const ExpansionTrigger = memo(({
    item,
    onToggle,
    isSdaFormat = false,
}: {
    item: ExpandableItem;
    onToggle: (e: React.MouseEvent) => void;
    isSdaFormat?: boolean;
}) => {
    // 1. Logic for choosing the icon is now INSIDE this component.
    const Icon = ArrowRight;
    const isExpandable = item.hasContent || item.hasChildren;

    if (!isExpandable) {
        return (
            <div className="flex flex-1 items-center">
                <span className="w-7" /> {/* Spacer to align with icons */}
                <span className="flex-1 flex flex-row items-center gap-2 pr-2 min-w-0">
                    <PathBreadcrumb groupHeaderId={item.id} parents={item.parents || []} hasContents={false} isSdaFormat={isSdaFormat} />
                </span>
            </div>
        );
    }

    return (
        <button
            onClick={onToggle}
            aria-expanded={item.isExpanded}
            aria-controls={`content-${item.id}`}
            className="flex flex-1 items-center hover:bg-accent/10 rounded-sm group text-left w-full"
        >
            <Icon className={cn("h-4 w-4 xl4:h-6 xl:w-6 text-zinc-600 ml-1 group-hover:text-zinc-950 flex-shrink-0 transition-transform duration-200", item.isExpanded && "rotate-90")} />
            <span className="flex-1 flex flex-row items-center gap-2 pl-3 pr-2 min-w-0">
                <PathBreadcrumb groupHeaderId={item.id} parents={item.parents || []} hasContents={item.hasContent} isSdaFormat={isSdaFormat} />
            </span>
        </button>
    );
});

const MetricsSection = memo(({
    item,
    isEditMode
}: {
    item: FlattenedItem
    isEditMode: boolean
}) => {
    const convertedScore = useMemo(() =>
        convertAverage(parseFloat(item.score)),
        [item.score]
    );

    const { count, includedCount, ratedCount, score, ratingTypes } = item;

    return (
        <div className="flex gap-2 items-center px-2">
            {/* {process.env.NODE_ENV === 'development' && (
                <div className="flex gap-1 scale-90">
                    <CountPill count={item.fastFillCount} type="fastfill" className="hidden md:flex" />
                    <CountPill count={item.missingCount} type="missing" className="hidden md:flex" />
                    <CountPill count={item.nonFinalCount} type="status" className="hidden md:flex" />
                    <CountPill count={item.excludedCount} type="excluded" className="hidden md:flex" />
                </div>
            )} */}

            <div className="w-16 flex items-start">
                <ValuePill
                    iconOnly
                    size="tiny"
                    label="Tests"
                    icon={<TestsIcon className="h-4 w-4 text-zinc-500" />}
                    value={isEditMode ? `${includedCount}/${count}` : `${includedCount}`}

                />
            </div>
            <ScoreDisplay score={score} count={ratedCount ?? 0} />
            <div className="hidden md:block">
                <RatingMixedScore
                    width={140}
                    height={36}
                    type={ratingTypes as any}
                    score={convertedScore} />
            </div>
        </div>
    );
});

const RowHeader: React.FC<RowHeaderProps> = ({
    item,
    toggleExpand,
    active,
    className,
}) => {
    const showFastQA = false;

    // Test count / average / rating iconography don't apply to the compact SDA group heading
    // (raytd/web#106) — non-SDA headings keep the full metrics block.
    const { isEditMode, isSdaFormat } = useReportViewContext();

    // This callback function remains the same.
    const handleExpand = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        toggleExpand(item.id);
    }, [item.id, toggleExpand]);

    const expandableItemProps = useMemo(() => ({
        id: item.id,
        hasChildren: item.hasChildren,
        parents: item.parents,
        hasContent: item.hasContent,
        isExpanded: item.isExpanded,
        // Create a unique ID for ARIA attributes
        contentId: `content-${item.id}`,
    }), [item]);

    const isBaseLevel = item.level === 0;

    return (
        <div
            data-group-header="true"
            className={cn(
                "w-full border-b h-[44px] flex items-center",
                {
                    // "bg-yellow-50": active,
                    // "bg-white/5": !active,
                    "border-zinc-200": !isBaseLevel,
                    "border-t border-t-zinc-400": isBaseLevel,
                    "border-b ": isBaseLevel && !item.isExpanded,
                    'bg-zinc-100': ((item.hasContent || item.hasChildren) && !item.isExpanded)
                },
                className
            )}
        >
            <div className={cn(
                "flex items-center flex-1"
            )}>
                {(item.hasChildren || item.hasContent) ? (
                    <ExpansionTrigger
                        item={expandableItemProps}
                        // 1. Prop renamed from `handleExpand` to `onToggle` for clarity.
                        onToggle={handleExpand}
                        // 2. The `IconType` prop is completely removed.
                        isSdaFormat={isSdaFormat}
                    />
                ) : (
                    <>
                        <span className="w-[28px]" />
                        <span className="flex-1 font-medium flex flex-row items-center gap-2">
                            <PathBreadcrumb groupHeaderId={item.id} parents={item.parents || []} hasContents={false} isSdaFormat={isSdaFormat} />
                        </span>
                    </>
                )}

                {showFastQA && item.fastFillCount > 0 && (<div>{item.fastFillCount}</div>)}

                {!isSdaFormat && (
                    <MetricsSection
                        item={item}
                        isEditMode={isEditMode}
                    />
                )}
            </div>
        </div>
    );
};

export default memo(RowHeader);
